import { withLDConsumer as featureFlagClient } from 'launchdarkly-react-client-sdk';
import { useSelector, useDispatch } from 'react-redux';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import ShippingCollapsedData from '../../modules/shipping/components/collapsed-data';
import { setPaymentMethod, currentPaymentMethod } from '../../modules/payment/slice';
import BillingCollapsedData from '../../modules/billing/components/collapsed-data';
import { selectMethods, selectCardAddress, selectShipping } from '../../modules/shipping/slice';
import AdditionalServiceWrapper from '../../modules/additional-service/containers/additional-service-wrapper';
import useApiPostOrderError from '../../hooks/useApiPostOrderError';
import Shipping from '../../modules/shipping/containers/shipping';
import { EVENT, trigger } from '../../commons/utils/custom-event';
import { useCheckoutSession } from '../../helpers/sessionHelper';
import { status, paymentIdList } from '../../helpers/constants';
import Billing from '../../modules/billing/containers/billing';
import windowDimensions from '../../hooks/useWindowDimensions';
import usePortalSettings from '../../hooks/usePortalSettings';
import { initAnalytics } from '../../helpers/analyticsHelper';
import useApiStatus from '../../store/apiStatus/useApiStatus';
import { pluginList } from '../../commons/constants';
import { selectBilling } from '../../modules/billing/slice';
import autofillParser from '../../helpers/autofillParser';
import { useEmailPostApi } from '../../hooks/useAxios';
import { useSkeleton } from '../../hooks/useSkeleton';
import { useProduct } from '../../hooks/useProduct';
import DataContext from '../../store/dataContext';
import UiContext from '../../store/uiContext';
import useCurrency from '../../modules/currency/hooks/use-currency';
import { usePickup } from '../../modules/shipping/containers/pickup-in-store/use-pickup';
import Header from '../../commons/components/header';

import PaymentMethodSelected from '../PaymentMethodSelected';
import SkeletonLoading from '../SkeletonLoading';
import PaymentMethods from '../PaymentMethods';
import BillingPhone from '../BillingPhone';
import { CartTotalsMobile } from '../Cart';
import WelcomeBack from '../WelcomeBack';
import StoreCredit from '../StoreCredit';
import PaymentAdd from '../PaymentAdd';
import WarnTerms from './WarnTerms';
import UserAdd from '../UserAdd';
import { OrderNotesInput } from '../OrderNotes';
import './style.scss';

const Checkout = ({ setOrderSubmitting, flags }) => {
  const { resumeSession, isSessionActive } = useCheckoutSession();
  const { portalSettings } = usePortalSettings();
  const { isMobileDevice } = windowDimensions();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    apiResponse: apiEmailResponse,
    apiError: apiEmailError,
    apiLoading: apiEmailLoading,
    apiPostEmail,
  } = useEmailPostApi();
  const { apiStatus, isLoading: isLoadingApiStatus } = useApiStatus();
  const { allDigital } = useProduct();
  const shippingMethods = useSelector(selectMethods);
  const shippingCardAddress = useSelector(selectCardAddress);
  const selectedMethod = useSelector(currentPaymentMethod);
  const billingInfo = useSelector(selectBilling);
  const { status: shippingStatus, bopis } = useSelector(selectShipping);

  const dataContext = useContext(DataContext);
  const uiContext = useContext(UiContext);

  const { hasBillingError } = useApiPostOrderError();
  const { showLoading } = useSkeleton();

  const { getShoppercurrency } = useCurrency();

  const { isPickupActive } = usePickup();

  const creditCardType = selectedMethod === paymentIdList.CREDIT_CARD;

  const focusShippingName = () => {
    setTimeout(() => {
      const shippingField = document.getElementById('shipping-name');
      const billingField = document.getElementById('billing-name');
      if (shippingField) {
        shippingField.focus();
      } else if (billingField) {
        billingField.focus();
      }
    }, 0);
  };

  useEffect(() => {
    const hasEmailResponse = dataContext.apiEmailResponse && Object.values(dataContext.apiEmailResponse).length > 0;
    if (
      (dataContext.isLoggedIn === false && !hasEmailResponse) ||
      (isSessionActive && dataContext.isLoggedIn === false && !hasEmailResponse)
    ) {
      resumeSession();
    }
  }, [dataContext.isLoggedIn, isSessionActive]);

  useEffect(() => {
    if (apiEmailResponse && !dataContext.apiEmailResponse.success) {
      uiContext.setShowUserAddControl(false);
      initAnalytics({
        userId: dataContext.email,
        merchantId: dataContext?.merchantId,
        platformCartId: dataContext?.platformCartId,
        storeUrl: dataContext?.storeUrl,
      });
      dataContext.updateCheckoutSession('email', dataContext.email);
      dataContext.setApiEmailResponse(apiEmailResponse);
      const autofillInfo = autofillParser(apiEmailResponse);
      dataContext.updateCompanyName(autofillInfo.shipCompany);
      dataContext.updateContextShippingAddress(autofillInfo.contextShippingAddress);
      uiContext.updateCheckoutState(uiContext.TO_SHIPPING_ADDRESS_ADD);
      if (autofillInfo.isAvailable) {
        uiContext.setAllowShippingAddressAddShowControl(true);
        dispatch(setPaymentMethod(selectedMethod || paymentIdList.CREDIT_CARD));
      } else {
        uiContext.setAllowShippingAddressAddShowControl(false);
      }
      focusShippingName();
      trigger(EVENT.EMAIL_RESPONSE_READY, apiEmailResponse);
    }
  }, [apiEmailResponse]);

  const handleUserAdd = emailTerm => {
    apiPostEmail({ cartId: dataContext.cartId, deviceId: dataContext.device?.id }, emailTerm);
  };

  const renderMobileExposedView = () => {
    const isNotThankYouPage = !uiContext.done;
    const markup = [];

    if (isNotThankYouPage && isMobileDevice) {
      const isMobileViewAndExposed =
        portalSettings.orderNotes.isEnabled && portalSettings.orderNotes.mobileView === 'Exposed';

      if (isMobileViewAndExposed) {
        markup.push(<OrderNotesInput key="order-notes" />);
      }

      if (portalSettings.shippingProtection.isEnabled) {
        markup.push(
          <AdditionalServiceWrapper
            key="shipping-protection"
            type={pluginList.shippingProtection}
            isProtectionApplied={dataContext?.isProtectionApplied}
            isSignatureApplied={dataContext?.isSignatureApplied}
            shopperCurrency={getShoppercurrency()}
            isAppMainLoading={dataContext.showLoading}
            isLoadingProtection={uiContext?.isLoadingProtection}
            isLoadingSignature={uiContext?.isLoadingSignature}
            shippingStatus={shippingStatus}
            formStatus={uiContext?.formStatus}
            isLoadingCoupons={uiContext?.isLoadingCoupons}
          />,
        );
      }

      if (portalSettings.customerSignature.isEnabled) {
        markup.push(
          <AdditionalServiceWrapper
            key="custumer-signature"
            type={pluginList.custumerSignature}
            isProtectionApplied={dataContext?.isProtectionApplied}
            isSignatureApplied={dataContext?.isSignatureApplied}
            shopperCurrency={getShoppercurrency()}
            isAppMainLoading={dataContext.showLoading}
            isLoadingProtection={uiContext?.isLoadingProtection}
            isLoadingSignature={uiContext?.isLoadingSignature}
            shippingStatus={shippingStatus}
            formStatus={uiContext?.formStatus}
            isLoadingCoupons={uiContext?.isLoadingCoupons}
          />,
        );
      }
    }

    return markup;
  };

  const isDisabledWelcomeBack = flags['disable-welcome-back'];

  const toggleCollapse = () => {
    dataContext.updateSameBilling(!dataContext.sameBilling);
    uiContext.setIsShippingSectionCollapsed(!uiContext.isShippingSectionCollapsed);
  };

  const isBillingSectionUnCollapsed =
    hasBillingError() && uiContext.isShippingSectionCollapsed && uiContext.allowBillingAddressAdd;

  const showBillingStandalone =
    !isBillingSectionUnCollapsed &&
    !dataContext.sameBilling &&
    dataContext.shippingMethodId &&
    portalSettings?.checkoutMode?.mode !== 'ACTIVE';

  return (
    <div
      className={classNames({
        section: true,
        declined: uiContext.isCardDeclined.error,
      })}
    >
      <Header />
      <div className="p20">
        <UserAdd handleUserAdd={handleUserAdd} apiEmailError={apiEmailError} apiEmailLoading={apiEmailLoading} />
        {showLoading && <SkeletonLoading hideRight />}
        <div className={showLoading ? 'hide-wrapper' : ''}>
          <div className={`${!uiContext.allowBillingAddressAdd ? 'content-wrapper' : 'billing-wrapper'}`}>
            <div className={uiContext.isShippingSectionCollapsed ? 'is-hidden' : ''}>
              {!isDisabledWelcomeBack && <WelcomeBack />}
              {dataContext.cartDetails?.lineItems?.length > 0 && dataContext.isTotalsLoaded && !allDigital() ? (
                <Shipping
                  apiEmailResponse={dataContext.apiEmailResponse}
                  cartId={dataContext.cartId}
                  isLoadingExternalData={apiStatus.isEmailReady === status.LOADING || isLoadingApiStatus}
                  isFieldsetDisabled={
                    uiContext.checkoutStage === uiContext.TO_USER_ADD || apiStatus.isEmailReady === status.LOADING
                  }
                  updateCheckoutState={uiContext.updateCheckoutState}
                  TO_SHIPPING_ADDRESS_ADD={uiContext.TO_SHIPPING_ADDRESS_ADD}
                  isLoggedIn={dataContext.isLoggedIn}
                  setApiShippingAddressResponse={dataContext.setApiShippingAddressResponse}
                  setPaymentMethodData={dataContext.setPaymentMethodData}
                  shopperCurrency={getShoppercurrency()}
                  theme={dataContext.portalSettings.theme}
                  updateContextShippingAddress={dataContext.updateContextShippingAddress}
                  updateGiftInfo={dataContext.updateGiftInfo}
                  updateShippingMethodId={dataContext.updateShippingMethodId}
                  isAdditionalServiceLoading={uiContext.isLoadingProtection || uiContext.isLoadingSignature}
                  giftCertificates={dataContext.giftCertificates}
                  isLoadingCoupons={uiContext.isLoadingCoupons || uiContext.formStatus === status.LOADING}
                  isLoadingSignature={uiContext.isLoadingSignature}
                  isLoadingProtection={uiContext.isLoadingProtection}
                  additionalCharges={dataContext.additionalCharges}
                  cartTotals={dataContext.cartTotals}
                />
              ) : (
                dataContext.cartDetails?.lineItems?.length > 0 &&
                dataContext.isTotalsLoaded && (
                  <Billing
                    isFieldsetDisabled={
                      uiContext.checkoutStage === uiContext.TO_USER_ADD || apiStatus.isEmailReady === status.LOADING
                    }
                    theme={dataContext.portalSettings.theme}
                    setContextBillingAddress={dataContext.setContextBillingAddress}
                  />
                )
              )}
            </div>
            {uiContext.isShippingSectionCollapsed && (
              <ShippingCollapsedData
                address={shippingCardAddress.address}
                apartmentUnit={shippingCardAddress.apartmentUnit}
                companyName={shippingCardAddress.companyName}
                fullName={shippingCardAddress.fullName}
                methods={shippingMethods}
                shopperCurrency={getShoppercurrency()}
                title={t('shipping')}
                toggleCollapse={toggleCollapse}
              />
            )}
            {((uiContext.allowBillingAddressAdd && portalSettings?.checkoutMode?.mode === 'ACTIVE') ||
              uiContext.isCardDeclined.error) && (
              <h3 className="form-block-title active" id="checkout.billing_information">
                {t('checkout.billing_information')}
              </h3>
            )}
            {!flags['disable-store-credit'] && dataContext.apiEmailResponse?.availableStoreCredit > 0 && (
              <StoreCredit />
            )}
            {(isBillingSectionUnCollapsed || showBillingStandalone || isPickupActive()) && (
              <Billing
                isFieldsetDisabled={
                  uiContext.checkoutStage === uiContext.TO_USER_ADD || apiStatus.isEmailReady === status.LOADING
                }
                theme={dataContext.portalSettings.theme}
                setContextBillingAddress={dataContext.setContextBillingAddress}
              >
                {!bopis.isEnabled && <BillingPhone flags={flags} isBillingAddress />}
              </Billing>
            )}
            <div
              style={{
                display:
                  showBillingStandalone ||
                  isBillingSectionUnCollapsed ||
                  uiContext.isShippingSectionCollapsed ||
                  !uiContext.allowBillingAddressAdd
                    ? 'block'
                    : 'none',
                position: dataContext.areSuggestionsVisible ? 'relative' : 'static',
              }}
            >
              <PaymentMethods flags={flags} />
              <PaymentMethodSelected flags={flags} setOrderSubmitting={setOrderSubmitting} />
            </div>
            {!uiContext.isShippingSectionCollapsed && uiContext.allowBillingAddressAdd && !showBillingStandalone && (
              <BillingCollapsedData
                address={billingInfo.address.userInputSingleLine}
                apartmentUnit=""
                cardType={dataContext.cardType}
                fullName={billingInfo.fullName.value}
                last4Numbers={dataContext.last4Numbers}
                phoneNumber={dataContext.billingPhone}
                title={t('billing')}
                toggleCollapse={toggleCollapse}
              />
            )}
            {creditCardType && <CartTotalsMobile />}
            {renderMobileExposedView()}
            {creditCardType &&
              (selectedMethod === paymentIdList.CREDIT_CARD ||
                selectedMethod === paymentIdList.UNDEFINED ||
                selectedMethod === null) && <PaymentAdd />}
            <WarnTerms />
          </div>
        </div>
      </div>
    </div>
  );
};

Checkout.propTypes = {
  setOrderSubmitting: PropTypes.func.isRequired,
  flags: PropTypes.shape({
    'disable-google-analytics': PropTypes.bool,
    'disable-paypal': PropTypes.bool,
    'disable-welcome-back': PropTypes.bool,
    'disable-store-credit': PropTypes.bool,
    'disable-ddi-phone': PropTypes.bool,
  }),
};

export default featureFlagClient()(Checkout);
